<template>
  <div class="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
    <div class="w-full px-6 py-8">
      <div class="space-y-8">

        <!-- Header -->
        <div class="relative overflow-hidden bg-white shadow-2xl rounded-3xl border border-gray-100">
          <div class="absolute inset-0 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 opacity-5"></div>
          <div class="absolute top-0 right-0 -mt-8 -mr-8 w-40 h-40 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full opacity-10"></div>
          <div class="absolute bottom-0 left-0 -mb-8 -ml-8 w-32 h-32 bg-gradient-to-tr from-purple-400 to-pink-500 rounded-full opacity-10"></div>
          <div class="relative px-8 py-10 lg:px-12">
            <div class="flex flex-col lg:flex-row items-center lg:items-start space-y-4 lg:space-y-0 lg:space-x-8">
              <div class="flex-shrink-0">
                <div class="h-28 w-28 rounded-full bg-gradient-to-br from-blue-500 via-purple-600 to-pink-500 flex items-center justify-center shadow-2xl ring-8 ring-white/50">
                  <span class="text-4xl font-bold text-white">{{ user?.name?.charAt(0)?.toUpperCase() || '?' }}</span>
                </div>
              </div>
              <div class="flex-1 text-center lg:text-left">
                <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  <div>
                    <h1 class="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-gray-900 via-gray-800 to-gray-700 bg-clip-text text-transparent">
                      {{ user?.name || 'Cargando...' }}
                    </h1>
                    <p class="text-lg text-gray-600 mt-1">{{ user?.email }}</p>
                  </div>
                  <router-link
                    :to="`/user/${userId}`"
                    class="inline-flex items-center px-5 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-200 rounded-xl shadow-sm hover:bg-gray-50 transition-colors flex-shrink-0"
                  >
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    Ver perfil
                  </router-link>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="bg-white rounded-2xl shadow-xl p-12 text-center border border-gray-100">
          <div class="inline-block animate-spin rounded-full h-12 w-12 border-4 border-indigo-200 border-t-indigo-600"></div>
          <p class="mt-4 text-gray-500">Cargando datos del usuario...</p>
        </div>

        <template v-else-if="user">
          <!-- Edit Form -->
          <div class="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            <div class="bg-gradient-to-r from-blue-600 to-indigo-700 px-8 py-5">
              <h2 class="text-xl font-bold text-white flex items-center gap-3">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Información del usuario
              </h2>
            </div>
            <div class="p-8">
              <form @submit.prevent="saveUser" class="space-y-6">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label class="block text-sm font-semibold text-gray-700 mb-2">Nombre completo</label>
                    <input
                      v-model="form.name"
                      type="text"
                      required
                      class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    />
                  </div>
                  <div>
                    <label class="block text-sm font-semibold text-gray-700 mb-2">Correo electrónico</label>
                    <input
                      v-model="form.email"
                      type="email"
                      required
                      class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    />
                  </div>
                  <div class="md:col-span-2">
                    <label class="block text-sm font-semibold text-gray-700 mb-2">Nueva contraseña <span class="text-gray-400 font-normal">(dejar en blanco para no cambiar)</span></label>
                    <input
                      v-model="form.password"
                      type="password"
                      minlength="6"
                      placeholder="Mínimo 6 caracteres"
                      class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    />
                  </div>
                </div>

                <div class="flex justify-end gap-3 pt-4 border-t border-gray-100">
                  <router-link
                    :to="`/user/${userId}`"
                    class="px-5 py-2.5 text-sm font-semibold rounded-xl transition-colors"
                    style="background-color:#475569;color:#fff"
                  >Cancelar</router-link>
                  <button
                    type="submit"
                    :disabled="saving"
                    class="px-5 py-2.5 text-sm font-semibold rounded-xl transition-opacity hover:opacity-90 disabled:opacity-60"
                    style="background-color:#1d4ed8;color:#fff"
                  >{{ saving ? 'Guardando...' : 'Guardar cambios' }}</button>
                </div>
              </form>
            </div>
          </div>

          <!-- Block/Unblock Section -->
          <div class="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            <div class="bg-gradient-to-r from-orange-500 to-red-600 px-8 py-5">
              <h2 class="text-xl font-bold text-white flex items-center gap-3">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
                Acceso al aplicativo
              </h2>
            </div>
            <div class="p-8">
              <div v-if="user.isBlocked" class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-red-50 border border-red-200 rounded-xl p-5">
                <div>
                  <p class="font-semibold text-red-800">Usuario bloqueado</p>
                  <p v-if="user.blockedReason" class="text-sm text-red-700 mt-0.5">Motivo: {{ user.blockedReason }}</p>
                  <p v-if="user.blockedUntil" class="text-sm text-red-600 mt-0.5">
                    Hasta: {{ new Date(user.blockedUntil).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' }) }}
                  </p>
                  <p v-else class="text-sm text-red-600 mt-0.5">Bloqueo indefinido</p>
                </div>
                <button
                  @click="doUnblock"
                  :disabled="blockLoading"
                  class="flex-shrink-0 px-5 py-2.5 text-sm font-semibold rounded-xl disabled:opacity-60 transition-opacity hover:opacity-90"
                  style="background-color:#16a34a;color:#fff"
                >{{ blockLoading ? 'Desbloqueando...' : 'Desbloquear' }}</button>
              </div>
              <div v-else class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-green-50 border border-green-200 rounded-xl p-5">
                <div class="flex items-center gap-3">
                  <div class="w-9 h-9 bg-green-100 rounded-lg flex items-center justify-center">
                    <svg class="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <p class="font-semibold text-green-800">Usuario activo — Sin restricciones</p>
                </div>
                <button
                  @click="showBlockPanel = !showBlockPanel"
                  class="flex-shrink-0 px-5 py-2.5 text-sm font-semibold rounded-xl transition-opacity hover:opacity-90"
                  style="background-color:#ea580c;color:#fff"
                >Bloquear usuario</button>
              </div>

              <div v-if="showBlockPanel && !user.isBlocked" class="mt-6 space-y-4">
                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-2">Duración del bloqueo</label>
                  <div class="flex flex-wrap gap-2">
                    <button
                      v-for="opt in blockDurationOptions"
                      :key="opt.value"
                      type="button"
                      @click="blockDuration = opt.value"
                      class="px-3 py-1.5 text-sm rounded-lg border-2 transition-all"
                      :class="blockDuration === opt.value ? 'border-red-500 bg-red-50 text-red-700 font-medium' : 'border-gray-200 text-gray-600 hover:border-gray-300'"
                    >{{ opt.label }}</button>
                  </div>
                </div>
                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-2">Motivo <span class="text-gray-400 font-normal">(opcional)</span></label>
                  <textarea
                    v-model="blockReason"
                    rows="2"
                    placeholder="Ej: Comportamiento inapropiado..."
                    class="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
                  ></textarea>
                </div>
                <div class="flex justify-end gap-3">
                  <button
                    type="button"
                    @click="showBlockPanel = false"
                    class="px-4 py-2 text-sm font-semibold rounded-xl"
                    style="background-color:#475569;color:#fff"
                  >Cancelar</button>
                  <button
                    type="button"
                    @click="doBlock"
                    :disabled="blockLoading"
                    class="px-4 py-2 text-sm font-semibold rounded-xl disabled:opacity-60 transition-opacity hover:opacity-90"
                    style="background-color:#be123c;color:#fff"
                  >{{ blockLoading ? 'Bloqueando...' : 'Confirmar bloqueo' }}</button>
                </div>
              </div>
            </div>
          </div>

          <!-- Subscription Section -->
          <div class="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            <div class="bg-gradient-to-r from-yellow-500 to-amber-600 px-8 py-5">
              <h2 class="text-xl font-bold text-white flex items-center gap-3">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Suscripción
              </h2>
            </div>
            <div class="p-8">
              <div v-if="subLoading" class="text-sm text-gray-400">Cargando...</div>
              <div v-else-if="subscription" class="space-y-4">
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-3">
                    <span
                      class="px-3 py-1 rounded-full text-sm font-bold"
                      :style="subscription.plan.type === 'premium' ? 'background:#fef3c7;color:#92400e' : 'background:#f3f4f6;color:#374151'"
                    >{{ subscription.plan.type === 'premium' ? 'Premium' : 'Free' }}</span>
                    <span class="text-base font-semibold text-gray-900">{{ subscription.plan.name }}</span>
                  </div>
                  <button
                    @click="showGrantForm = !showGrantForm"
                    class="px-4 py-2 text-sm font-semibold rounded-xl transition-opacity hover:opacity-90"
                    style="background-color:#1d4ed8;color:#fff"
                  >Otorgar Premium</button>
                </div>
                <div class="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4">
                  <div class="bg-blue-50 rounded-xl p-4 text-center border border-blue-100">
                    <p class="text-2xl font-bold text-blue-700">{{ subscription.plan.likesPerDay === -1 ? '∞' : subscription.plan.likesPerDay }}</p>
                    <p class="text-xs font-medium text-blue-600 mt-1">Likes/día</p>
                  </div>
                  <div class="bg-purple-50 rounded-xl p-4 text-center border border-purple-100">
                    <p class="text-2xl font-bold text-purple-700">{{ subscription.plan.superLikesPerDay }}</p>
                    <p class="text-xs font-medium text-purple-600 mt-1">Super-likes/día</p>
                  </div>
                  <div class="bg-green-50 rounded-xl p-4 text-center border border-green-100">
                    <p class="text-2xl font-bold text-green-700">{{ subscription.plan.maxPets }}</p>
                    <p class="text-xs font-medium text-green-600 mt-1">Máx. mascotas</p>
                  </div>
                  <div class="bg-orange-50 rounded-xl p-4 text-center border border-orange-100">
                    <p class="text-sm font-bold text-orange-700">{{ subscription.subscription?.expiresAt ? formatDate(subscription.subscription.expiresAt) : 'Sin límite' }}</p>
                    <p class="text-xs font-medium text-orange-600 mt-1">Vencimiento</p>
                  </div>
                </div>
              </div>
              <div v-else class="flex items-center justify-between">
                <p class="text-sm text-gray-500">Sin suscripción activa (plan Free)</p>
                <button
                  @click="showGrantForm = !showGrantForm"
                  class="px-4 py-2 text-sm font-semibold rounded-xl transition-opacity hover:opacity-90"
                  style="background-color:#1d4ed8;color:#fff"
                >Otorgar Premium</button>
              </div>

              <div v-if="showGrantForm" class="mt-6 space-y-4 border-t border-gray-100 pt-6">
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label class="block text-sm font-semibold text-gray-700 mb-2">Plan</label>
                    <select v-model="grantForm.planId" class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option v-for="p in premiumPlans" :key="p.id" :value="p.id">{{ p.name }}</option>
                    </select>
                  </div>
                  <div>
                    <label class="block text-sm font-semibold text-gray-700 mb-2">Días a otorgar</label>
                    <input v-model="grantForm.days" type="number" min="1" class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                </div>
                <div class="flex justify-end gap-3">
                  <button type="button" @click="showGrantForm = false" class="px-4 py-2 text-sm font-semibold rounded-xl" style="background-color:#475569;color:#fff">Cancelar</button>
                  <button type="button" @click="doGrantPremium" :disabled="grantLoading" class="px-4 py-2 text-sm font-semibold rounded-xl disabled:opacity-60 transition-opacity hover:opacity-90" style="background-color:#1d4ed8;color:#fff">
                    {{ grantLoading ? 'Procesando...' : 'Otorgar' }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Streak Section -->
          <div class="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            <div class="bg-gradient-to-r from-indigo-600 to-purple-700 px-8 py-5">
              <h2 class="text-xl font-bold text-white flex items-center gap-3">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
                </svg>
                Racha de login
              </h2>
            </div>
            <div class="p-8">
              <div v-if="subLoading" class="text-sm text-gray-400">Cargando...</div>
              <div v-else class="grid grid-cols-3 gap-4">
                <div class="bg-indigo-50 rounded-xl p-5 text-center border border-indigo-100">
                  <p class="text-4xl font-bold text-indigo-700">{{ streak?.currentStreak || 0 }}</p>
                  <p class="text-sm font-medium text-indigo-600 mt-2">Racha actual</p>
                </div>
                <div class="bg-purple-50 rounded-xl p-5 text-center border border-purple-100">
                  <p class="text-4xl font-bold text-purple-700">{{ streak?.longestStreak || 0 }}</p>
                  <p class="text-sm font-medium text-purple-600 mt-2">Récord</p>
                </div>
                <div class="bg-green-50 rounded-xl p-5 text-center border border-green-100">
                  <p class="text-4xl font-bold text-green-700">{{ streak?.totalLogins || 0 }}</p>
                  <p class="text-sm font-medium text-green-600 mt-2">Logins totales</p>
                </div>
              </div>
              <p v-if="streak?.lastLoginDate" class="text-xs text-gray-400 mt-5 text-center">
                Último login: {{ formatDate(streak.lastLoginDate) }}
              </p>
            </div>
          </div>
        </template>

      </div>
    </div>
  </div>

  <ConfirmModal
    :show="showPasswordConfirm"
    title="Cambiar contraseña"
    message="¿Estás seguro de que querés cambiar la contraseña de este usuario?"
    confirm-text="Sí, cambiar"
    cancel-text="Cancelar"
    @confirm="doSaveUser"
    @close="showPasswordConfirm = false"
  />
</template>

<script>
import { ref, reactive, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useToast } from 'vue-toastification'
import apiService from '../services/api'
import { subscriptionService } from '../services/subscriptionService'
import ConfirmModal from '../components/ConfirmModal.vue'

export default {
  name: 'EditUser',
  components: { ConfirmModal },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const toast = useToast()
    const userId = route.params.id

    const user = ref(null)
    const loading = ref(false)
    const saving = ref(false)
    const form = reactive({ name: '', email: '', password: '' })
    const showPasswordConfirm = ref(false)

    const blockLoading = ref(false)
    const showBlockPanel = ref(false)
    const blockReason = ref('')
    const blockDuration = ref('indefinite')
    const blockDurationOptions = [
      { label: '1 día', value: '1d' },
      { label: '3 días', value: '3d' },
      { label: '7 días', value: '7d' },
      { label: '30 días', value: '30d' },
      { label: 'Indefinido', value: 'indefinite' }
    ]

    const subLoading = ref(false)
    const subscription = ref(null)
    const streak = ref(null)
    const showGrantForm = ref(false)
    const grantLoading = ref(false)
    const premiumPlans = ref([])
    const grantForm = reactive({ planId: null, days: 30 })

    const getBlockedUntil = () => {
      const map = { '1d': 1, '3d': 3, '7d': 7, '30d': 30 }
      if (!map[blockDuration.value]) return undefined
      const d = new Date()
      d.setDate(d.getDate() + map[blockDuration.value])
      return d.toISOString()
    }

    const loadUser = async () => {
      loading.value = true
      try {
        const res = await apiService.api.get(`/admin/users/${userId}`)
        user.value = res.data.data
        form.name = user.value.name
        form.email = user.value.email
        form.password = ''
      } catch {
        toast.error('Error al cargar el usuario')
      } finally {
        loading.value = false
      }
    }

    const loadSubscriptionData = async () => {
      subLoading.value = true
      try {
        const [subRes, streakRes] = await Promise.all([
          apiService.api.get(`/admin/subscriptions/users/${userId}/subscription`),
          apiService.api.get(`/admin/subscriptions/users/${userId}/streak`)
        ])
        subscription.value = subRes.data.data
        streak.value = streakRes.data.data
      } catch {
        subscription.value = null
        streak.value = null
      } finally {
        subLoading.value = false
      }
    }

    const loadPremiumPlans = async () => {
      try {
        const res = await subscriptionService.getPlans()
        premiumPlans.value = (res.data || []).filter((p) => p.type === 'premium' && p.isActive !== false)
        if (premiumPlans.value.length > 0) grantForm.planId = premiumPlans.value[0].id
      } catch {}
    }

    const saveUser = async () => {
      if (form.password) {
        showPasswordConfirm.value = true
        return
      }
      await doSaveUser()
    }

    const doSaveUser = async () => {
      showPasswordConfirm.value = false
      saving.value = true
      try {
        const payload = { name: form.name, email: form.email }
        if (form.password) payload.password = form.password
        await apiService.api.put(`/admin/users/${userId}`, payload)
        toast.success('Usuario actualizado')
        user.value = { ...user.value, name: form.name, email: form.email }
        form.password = ''
      } catch (err) {
        toast.error(err?.response?.data?.message || 'Error al guardar')
      } finally {
        saving.value = false
      }
    }

    const doBlock = async () => {
      blockLoading.value = true
      try {
        await apiService.api.patch(`/admin/users/${userId}/block`, {
          action: 'block',
          reason: blockReason.value || undefined,
          blockedUntil: getBlockedUntil()
        })
        user.value.isBlocked = true
        user.value.blockedReason = blockReason.value || null
        user.value.blockedUntil = getBlockedUntil() || null
        showBlockPanel.value = false
        toast.success('Usuario bloqueado')
      } catch {
        toast.error('Error al bloquear')
      } finally {
        blockLoading.value = false
      }
    }

    const doUnblock = async () => {
      blockLoading.value = true
      try {
        await apiService.api.patch(`/admin/users/${userId}/block`, { action: 'unblock' })
        user.value.isBlocked = false
        user.value.blockedReason = null
        user.value.blockedUntil = null
        toast.success('Usuario desbloqueado')
      } catch {
        toast.error('Error al desbloquear')
      } finally {
        blockLoading.value = false
      }
    }

    const doGrantPremium = async () => {
      grantLoading.value = true
      try {
        await subscriptionService.grantPremium(Number(userId), grantForm.planId, grantForm.days)
        toast.success('Premium otorgado correctamente')
        showGrantForm.value = false
        await loadSubscriptionData()
      } catch {
        toast.error('Error al otorgar premium')
      } finally {
        grantLoading.value = false
      }
    }

    const formatDate = (d) => {
      return new Date(d).toLocaleDateString('es-ES', { year: 'numeric', month: 'short', day: 'numeric' })
    }

    onMounted(async () => {
      await loadUser()
      loadSubscriptionData()
      loadPremiumPlans()
    })

    return {
      userId,
      user,
      loading,
      saving,
      form,
      showPasswordConfirm,
      blockLoading,
      showBlockPanel,
      blockReason,
      blockDuration,
      blockDurationOptions,
      subLoading,
      subscription,
      streak,
      showGrantForm,
      grantLoading,
      premiumPlans,
      grantForm,
      saveUser,
      doSaveUser,
      doBlock,
      doUnblock,
      doGrantPremium,
      formatDate
    }
  }
}
</script>
